/**
 * Canonical example envelopes shown to the user in the first-upload
 * consent prompt and on `/privacy` (PRD US 94, ADR-035, acceptance S37).
 *
 * The point: transparency by demonstration. The user sees literal JSON
 * of what each pipeline would actually send before agreeing.
 *
 * The S37 example-parity invariant lives in
 * `__tests__/promptExamples.test.ts`: running every Sentry-shaped
 * example through `beforeSend`/`beforeBreadcrumb` must yield the
 * identical object. If the test fails, the prompt's promise has
 * drifted from the production scrubbers — fix one or the other before
 * the prompt copy lands.
 */

import type { TelemetryEvent } from '@/domain/telemetry/types'
import { beforeBreadcrumb, beforeSend } from './scrubbers'
import type { SentryBreadcrumbLike, SentryEventLike } from './scrubbers'

export const plausibleEventExample: TelemetryEvent = {
  event: 'version_imported',
  props: {
    granularity: 'section',
    match_ratio: 0.87,
    unchanged: 42,
    changed: 5,
    added: 3,
    removed: 1,
    block_count_v1: 48,
    block_count_v2: 50,
  },
}

export const sentryErrorExample: SentryEventLike = {
  exception: {
    values: [
      {
        type: 'TypeError',
        value: "Cannot read properties of undefined (reading 'projects')",
      },
    ],
  },
  request: { url: '/review/:projectId' },
  transaction: '/review/:projectId',
}

export const sentryPerformanceExample: SentryEventLike = {
  transaction: '/library',
  request: { url: '/library' },
}

export const sentryBreadcrumbExample: SentryBreadcrumbLike = {
  category: 'fetch',
  data: { url: '/api/v1/health', method: 'GET', status_code: 200 },
}

export interface ScrubberParityFailure {
  example: string
  before: unknown
  after: unknown
}

/**
 * Returns the list of examples that differ from themselves after
 * scrubbing. An empty array means every example is already
 * content-free — i.e., what the prompt promises matches what an
 * envelope can actually carry.
 */
export function findScrubberParityFailures(): ScrubberParityFailure[] {
  const failures: ScrubberParityFailure[] = []

  const checks: Array<{
    name: string
    before: unknown
    after: unknown
  }> = [
    {
      name: 'sentryErrorExample',
      before: sentryErrorExample,
      after: beforeSend(sentryErrorExample),
    },
    {
      name: 'sentryPerformanceExample',
      before: sentryPerformanceExample,
      after: beforeSend(sentryPerformanceExample),
    },
    {
      name: 'sentryBreadcrumbExample',
      before: sentryBreadcrumbExample,
      after: beforeBreadcrumb(sentryBreadcrumbExample),
    },
  ]

  for (const { name, before, after } of checks) {
    if (JSON.stringify(before) !== JSON.stringify(after)) {
      failures.push({ example: name, before, after })
    }
  }

  return failures
}
